import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  Shield, Smartphone, Key, Users, Lock, RefreshCw, AlertTriangle, CheckCircle
} from 'lucide-react';
import axios from 'axios';

const Admin2FASettings = () => {
  const [settings, setSettings] = useState({
    enabled_for_admins: false,
    enabled_for_users: false,
    required_for_admins: false,
    required_for_users: false
  });
  const [usersWithin2FA, setUsersWithin2FA] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetModal, setResetModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const getAuthHeader = () => {
    const token = localStorage.getItem('admin_token');
    return { Authorization: `Bearer ${token}` };
  };

  useEffect(() => {
    fetchSettings();
    fetchUsersWithin2FA();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}/api/2fa/admin/settings`,
        { headers: getAuthHeader() }
      );
      setSettings(response.data.settings);
    } catch (err) {
      console.error('Error fetching settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsersWithin2FA = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/2fa/admin/users-with-2fa`,
        { headers: getAuthHeader() }
      );
      setUsersWithin2FA(response.data.users || []);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      await axios.put(
        `${API_URL}/api/2fa/admin/settings`,
        settings,
        { headers: getAuthHeader() }
      );
      alert('2FA settings saved successfully');
    } catch (err) {
      alert('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset2FA = async () => {
    if (!selectedUser) return;

    try {
      await axios.post(
        `${API_URL}/api/2fa/admin/reset/${selectedUser.id}`,
        {},
        { headers: getAuthHeader() }
      );
      alert('User 2FA has been reset');
      setResetModal(false);
      setSelectedUser(null);
      fetchUsersWithin2FA();
    } catch (err) {
      alert('Failed to reset 2FA');
    }
  };

  const toggleSetting = (key) => {
    setSettings(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Shield className="h-6 w-6" />
          Two-Factor Authentication Settings
        </h1>
      </div>

      {/* Warning Card */}
      <Card className="bg-amber-50 border-amber-200">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-6 w-6 text-amber-500 mt-0.5" />
            <div>
              <p className="font-medium text-amber-900">Important Security Notice</p>
              <p className="text-sm text-amber-700">
                Two-factor authentication adds an extra layer of security. When enabled,
                users will need to enter a code from their authenticator app in addition to their password.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Global Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5" />
            Global 2FA Settings
          </CardTitle>
          <CardDescription>
            Control 2FA availability for different user types
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {loading ? (
            <p className="text-center py-4 text-gray-500">Loading settings...</p>
          ) : (
            <>
              {/* Admin Settings */}
              <div className="border rounded-lg p-4 space-y-4">
                <h3 className="font-semibold flex items-center gap-2">
                  <Shield className="h-4 w-4" />
                  Admin Accounts
                </h3>
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Enable 2FA for Admins</Label>
                    <p className="text-sm text-gray-500">Allow admins to set up 2FA</p>
                  </div>
                  <Switch
                    checked={settings.enabled_for_admins}
                    onCheckedChange={() => toggleSetting('enabled_for_admins')}
                  />
                </div>
                {settings.enabled_for_admins && (
                  <div className="flex items-center justify-between pl-4 border-l-2 border-blue-200">
                    <div>
                      <Label>Require 2FA for Admins</Label>
                      <p className="text-sm text-gray-500">Force all admins to use 2FA</p>
                    </div>
                    <Switch
                      checked={settings.required_for_admins}
                      onCheckedChange={() => toggleSetting('required_for_admins')}
                    />
                  </div>
                )}
              </div>

              {/* User Settings */}
              <div className="border rounded-lg p-4 space-y-4">
                <h3 className="font-semibold flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Regular Users
                </h3>
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Enable 2FA for Users</Label>
                    <p className="text-sm text-gray-500">Allow users to set up 2FA</p>
                  </div>
                  <Switch
                    checked={settings.enabled_for_users}
                    onCheckedChange={() => toggleSetting('enabled_for_users')}
                  />
                </div>
                {settings.enabled_for_users && (
                  <div className="flex items-center justify-between pl-4 border-l-2 border-blue-200">
                    <div>
                      <Label>Require 2FA for Users</Label>
                      <p className="text-sm text-gray-500">Force all users to use 2FA</p>
                    </div>
                    <Switch
                      checked={settings.required_for_users}
                      onCheckedChange={() => toggleSetting('required_for_users')}
                    />
                  </div>
                )}
              </div>

              {/* Current Status */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium mb-3">Current Status</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    {settings.enabled_for_admins ? (
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    ) : (
                      <Lock className="h-4 w-4 text-gray-400" />
                    )}
                    <span>Admin 2FA: {settings.enabled_for_admins ? 'Enabled' : 'Disabled'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {settings.enabled_for_users ? (
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    ) : (
                      <Lock className="h-4 w-4 text-gray-400" />
                    )}
                    <span>User 2FA: {settings.enabled_for_users ? 'Enabled' : 'Disabled'}</span>
                  </div>
                </div>
              </div>

              <Button onClick={handleSaveSettings} disabled={saving} className="w-full">
                {saving ? 'Saving...' : 'Save Settings'}
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      {/* Users with 2FA */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Smartphone className="h-5 w-5" />
            Users with 2FA Enabled ({usersWithin2FA.length})
          </CardTitle>
          <CardDescription>
            Manage 2FA for individual users
          </CardDescription>
        </CardHeader>
        <CardContent>
          {usersWithin2FA.length === 0 ? (
            <p className="text-center py-8 text-gray-500">
              No users have enabled 2FA yet
            </p>
          ) : (
            <div className="space-y-3">
              {usersWithin2FA.map(user => (
                <div
                  key={user.id}
                  className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <Smartphone className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium">{user.name}</p>
                      <p className="text-sm text-gray-500">{user.email}</p>
                    </div>
                    {user.is_admin && (
                      <Badge className="bg-purple-100 text-purple-800">Admin</Badge>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSelectedUser(user);
                      setResetModal(true);
                    }}
                  >
                    <RefreshCw className="h-4 w-4 mr-1" />
                    Reset 2FA
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Reset Confirmation Modal */}
      <Dialog open={resetModal} onOpenChange={setResetModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Reset User 2FA
            </DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4">
              <p>
                Are you sure you want to reset 2FA for <strong>{selectedUser.name}</strong>?
              </p>
              <p className="text-sm text-gray-500">
                This will disable 2FA for this user. They will need to set it up again.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetModal(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleReset2FA}>
              Reset 2FA
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Admin2FASettings;
